/**
 * Composio Client Service (Frontend)
 * 
 * Frontend wrapper for the Composio Cloud Function API.
 * Handles authentication, caching, and type-safe API calls.
 */

import { getAuth } from 'firebase/auth';
// Capability-class constants: ONE frontend source (2026-08-18 consolidation)
// — roleAppAccessShared.ts, whose families/labels are lockstep-gated against
// functions/src/rbac/roleAppAccess.ts. Re-exported here so every existing
// ComposioClient importer keeps working. (Must sit at the top: CRA build
// enforces import/first.)
import type {
  AccessTier as SharedAccessTier,
  AccessProfile as SharedAccessProfile,
  CapabilityClass as SharedCapabilityClass,
} from '../../components/TeamRoles/roleAppAccessShared';
export {
  CAPABILITY_CLASS_LABELS,
  CAPABILITY_CLASS_SUMMARIES,
  profileFromTier,
  tierFromProfile,
} from '../../components/TeamRoles/roleAppAccessShared';

// Use Cloud Functions base URL
const API_BASE = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';
const COMPOSIO_ENDPOINT = `${API_BASE}/composioApi/composio`;

export interface ComposioToolkit {
  name: string;
  slug: string;
  auth_schemes: string[];
  composio_managed_auth_schemes?: string[];
  enabled?: boolean;
  no_auth?: boolean;
  meta: {
    tools_count: number;
    triggers_count: number;
    description: string;
    logo: string;
    categories: Array<{ id: string; name: string }>;
  };
}

/**
 * Toolkits whose managed OAuth still requires typed setup values BEFORE
 * sign-in can start (2026-08-17, founder callout: the WhatsApp tile said
 * "1-Click" and then demanded a WABA ID).
 *
 * This is the ONE piece of auth classification that lives here — the lane
 * classifier (connectLane.ts) imports it. Everything else moved there on
 * 2026-09-02: supportsOneClickAuth / connectsInOneClick / getManagedAuthLabel
 * were three overlapping answers to one question, none of which read
 * `no_auth` despite declaring it, and the label helper would have called a
 * non-OAuth managed scheme "One-click auth". connectLaneFor answers it once.
 */
export const TOOLKITS_WITH_SETUP_FIELDS = new Set<string>(['whatsapp']);

export interface ComposioTool {
  slug: string;
  name: string;
  description: string;
  toolkit: { slug: string; name: string; logo: string };
  input_parameters: Record<string, any>;
}

export interface ConnectedAccount {
  id: string;
  status: string;
  toolkit: { slug: string; name: string };
  created_at: string;
  /** The signed-in account's identity when the provider exposes it —
   *  email primary, phone second (2026-08-18 owner directive: every tile
   *  names WHICH account it is). */
  identity?: string;
  /** Post-connect mailbox health (2026-08-20 connect-journey batch): false
   *  means the signed-in identity has NO usable mailbox (the probe failed
   *  with a classified no-mailbox error) — the tile warns instead of
   *  presenting a green connection that fails at the first mail call. */
  mailboxOk?: boolean;
  /** Every ACTIVE connection for this toolkit, oldest first — [0] is the
   *  primary, matching the backend's earliest-active selection (task #49).
   *  More than one entry = the user connected multiple accounts. Rows that
   *  came in via a member share carry via/sharedByName so the roster can say
   *  "shared by <name>" instead of a positional "account N" (2026-08-20;
   *  another member's provider email is never exposed — house rule). */
  connections?: Array<{
    id: string;
    created_at: string;
    identity?: string;
    mailboxOk?: boolean;
    via?: 'organization' | 'shared';
    sharedByName?: string;
  }>;
}

/** Post-connect verification verdict (server: composio/connectionVerify). */
export interface ConnectionVerification {
  verdict: 'confirmed' | 'mismatch' | 'no-mailbox' | 'unverified';
  identity: string | null;
  identitySource: 'probe' | 'id_token' | null;
  mailboxOk: boolean | null;
  mailboxErrorClass: string | null;
  intendedAddress: string | null;
  possibleAlias: boolean;
  verifiedAtIso: string;
}

/**
 * Get the current user's Firebase ID token
 */
async function getIdToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

/**
 * Make an authenticated request to the Composio Cloud Function
 */
async function apiRequest<T>(
  path: string,
  method: string = 'GET',
  body?: any
): Promise<T> {
  const token = await getIdToken();
  const url = `${COMPOSIO_ENDPOINT}/${path}`;
  
  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  };
  if (body) options.body = JSON.stringify(body);

  const response = await fetch(url, options);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
    // Prefer the human-readable `message` over the machine `error` code
    // (2026-08-11): the scope/permission endpoints put remediation copy in
    // `message` ("Ask an owner or admin…", the folder-denial explanation) —
    // throwing the bare code showed users literals like 'permission_denied'.
    const err: any = new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
    // Machine code survives alongside the human message (2026-08-14) so
    // callers can branch (e.g. 'missing_fields' opens the setup-fields
    // prompt) without parsing prose.
    err.code = errorData.error;
    err.fields = errorData.fields;
    // Connect triage (2026-08-17): pathway chooser rides the 422 too.
    err.help = errorData.help;
    // Connect-lane rejection (2026-09-02): 'unsupported_auth' names the lane
    // the app CAN use, so the surface falls back instead of showing an error.
    err.lane = errorData.lane;
    err.authSchemes = errorData.authSchemes;
    throw err;
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

// ============================================================================
// Public API
// ============================================================================

/**
 * List all available toolkits (integrations)
 * Cached for 10 minutes since toolkit metadata is static
 */
// `live` marks data set by a fetch THIS session (vs adopted from
// localStorage): fresh backend truth is always serveable within the TTL,
// even when the backend is legitimately degraded to a sub-floor fragment —
// only CACHED copies are floor-gated (review findings, 2026-08-16).
let _toolkitsCache: { data: ComposioToolkit[]; ts: number; live?: boolean } | null = null;
const TOOLKITS_CACHE_TTL = 10 * 60 * 1000; // 10 minutes (in-memory)
// v2 (2026-08-16, catalog expansion live-test): v1 entries hold the old
// ~100-toolkit single page with a 24h TTL, so browsers that visited before
// the backend expansion kept rendering — and SEARCHING — the small list for
// up to a day ("there doesn't seem like there are more options"). New key =
// every client refetches once; the fragment guard below keeps any small
// list from ever pinning again.
const TOOLKITS_LOCAL_STORAGE_KEY = 'composio_toolkits_v2';
const TOOLKITS_LOCAL_STORAGE_TTL = 24 * 60 * 60 * 1000; // 24 hours
// The live catalog is ~1,209 toolkits; anything under this is a legacy page
// or a truncated fetch and must not be served as "the catalog".
const TOOLKITS_FULL_CATALOG_FLOOR = 500;

function readToolkitsFromLocalStorage(): { data: ComposioToolkit[]; ts: number } | null {
  try {
    const raw = window.localStorage.getItem(TOOLKITS_LOCAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.data) || typeof parsed?.ts !== 'number') {
      return null;
    }
    return { data: parsed.data, ts: parsed.ts };
  } catch {
    return null;
  }
}

function writeToolkitsToLocalStorage(cache: { data: ComposioToolkit[]; ts: number }): void {
  try {
    window.localStorage.setItem(TOOLKITS_LOCAL_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore storage errors (private mode, quota, etc.)
  }
}

async function fetchToolkitsAndUpdateCache(): Promise<ComposioToolkit[]> {
  const data = await apiRequest<{ toolkits: ComposioToolkit[] }>('toolkits');
  const toolkits = (data.toolkits || []).filter((tk) => tk.enabled !== false);
  _toolkitsCache = { data: toolkits, ts: Date.now(), live: true };
  // A degraded (sub-floor) fetch must never clobber a FULLER stored copy —
  // the stored full catalog is the stale-if-error fallback (mirrors the
  // backend's own fragment-vs-cache guard in composioService.listToolkits).
  const existing = readToolkitsFromLocalStorage();
  if (
    toolkits.length >= TOOLKITS_FULL_CATALOG_FLOOR ||
    !existing ||
    toolkits.length > existing.data.length
  ) {
    writeToolkitsToLocalStorage({ data: toolkits, ts: Date.now() });
  }
  try {
    // v1 held the pre-expansion ~100-toolkit page — drop the orphaned blob.
    window.localStorage.removeItem('composio_toolkits_v1');
  } catch { /* private mode / quota — ignore */ }
  return toolkits;
}

/**
 * SYNCHRONOUS catalog lookup — memory cache first, then the localStorage
 * copy. Never fetches (2026-09-02): its one caller is the connect-lane check
 * inside a click's own call stack, where the popup must be opened before any
 * await or the browser blocks it. `null` means "catalog not warm" — callers
 * must degrade, never block.
 */
export function peekToolkitBySlug(toolkitSlug: string): ComposioToolkit | null {
  const slug = String(toolkitSlug || '').toLowerCase();
  if (!slug) return null;
  const find = (list: ComposioToolkit[] | undefined): ComposioToolkit | null =>
    (list || []).find((tk) => String(tk.slug || '').toLowerCase() === slug) || null;
  const fromMemory = find(_toolkitsCache?.data);
  if (fromMemory) return fromMemory;
  const stored = readToolkitsFromLocalStorage();
  return find(stored?.data);
}

export async function listToolkits(): Promise<ComposioToolkit[]> {
  // A STORED list smaller than the full-catalog floor is a legacy fragment:
  // never serve it from cache — refetch instead. Data fetched live THIS
  // session is current backend truth and serves within the TTL regardless
  // (a legitimately degraded backend must not be hammered on every call).
  const isFullCatalog = (c: { data: ComposioToolkit[] } | null): boolean =>
    Boolean(c && c.data.length >= TOOLKITS_FULL_CATALOG_FLOOR);

  if (
    _toolkitsCache &&
    Date.now() - _toolkitsCache.ts < TOOLKITS_CACHE_TTL &&
    (_toolkitsCache.live || isFullCatalog(_toolkitsCache))
  ) {
    return _toolkitsCache.data;
  }

  const localStorageCache = readToolkitsFromLocalStorage();
  if (
    localStorageCache &&
    Date.now() - localStorageCache.ts < TOOLKITS_LOCAL_STORAGE_TTL &&
    isFullCatalog(localStorageCache)
  ) {
    _toolkitsCache = { ...localStorageCache, live: false };
    void fetchToolkitsAndUpdateCache().catch(() => {
      // Keep stale local cache on background refresh failure.
    });
    return localStorageCache.data;
  }

  try {
    return await fetchToolkitsAndUpdateCache();
  } catch (err) {
    // Stale-if-error: any servable data beats an empty integrations page.
    // Memory first (private mode has no localStorage at all), then storage.
    if (_toolkitsCache) return _toolkitsCache.data;
    if (localStorageCache) return localStorageCache.data;
    throw err;
  }
}

/**
 * List tools for specific toolkits
 */
export async function listTools(toolkitSlugs: string[]): Promise<ComposioTool[]> {
  const data = await apiRequest<{ tools: ComposioTool[] }>(
    `tools?toolkits=${toolkitSlugs.join(',')}`
  );
  return data.tools;
}

/**
 * Get current user's connected accounts
 */
export async function getConnectedAccounts(): Promise<ConnectedAccount[]> {
  const data = await apiRequest<{ accounts: any[] }>('accounts');
  // Normalize: Composio API may return appName/appUniqueId at top level instead of nested toolkit.
  // A row with NO status is treated as INITIATED, not ACTIVE (T1 live test,
  // 2026-08-12): the connection record exists from the moment OAuth is
  // initiated, so defaulting to ACTIVE showed a green "connected" check for
  // a consent screen the user never finished — and the app silently
  // "disconnected" later. Only an explicit ACTIVE may render as connected.
  const normalized = (data.accounts || []).map((a: any) => ({
    id: a.id,
    status: a.status || 'INITIATED',
    toolkit: {
      slug: a.toolkit?.slug || a.appUniqueId || a.appName || '',
      name: a.toolkit?.name || a.appName || a.appUniqueId || 'Unknown',
    },
    created_at: a.created_at || a.createdAt || '',
    // Signed-in account identity (2026-08-18) — server whitelist-extracts it.
    // The two-set normalizer below already keeps it; dropping it here left
    // onboarding and the in-chat card unable to say WHICH account is signed in.
    ...(typeof a.identity === 'string' && a.identity ? { identity: a.identity } : {}),
  }));
  // One entry per toolkit slug: the EARLIEST ACTIVE connection (matching the
  // backend's primary selection), with the full active roster attached so
  // multi-account UIs can see every connection.
  const bySlug = new Map<string, ConnectedAccount[]>();
  for (const acct of normalized) {
    const slug = acct.toolkit.slug.toLowerCase();
    if (!slug) continue;
    (bySlug.get(slug) ?? bySlug.set(slug, []).get(slug)!).push(acct);
  }
  const result: ConnectedAccount[] = [];
  for (const rows of bySlug.values()) {
    const actives = rows
      .filter((r) => r.status?.toUpperCase() === 'ACTIVE')
      .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    const primary = actives[0] || rows[0];
    result.push({
      ...primary,
      connections: actives.map((r) => ({ id: r.id, created_at: r.created_at, ...(r.identity ? { identity: r.identity } : {}) })),
    });
  }
  return result;
}

// ============================================================================
// Two-set model (personal vs organization, 2026-08-12)
// ============================================================================

/** C4a: a durable "waiting on an admin's approval" record — the connect
 *  timed out with a stated inbox, and approval may take days. Server
 *  self-heals it away once the toolkit reports ACTIVE. */
export interface PendingConnection {
  toolkitSlug: string;
  displayName: string;
  intendedAddress: string | null;
  requestedAtIso: string;
  status: 'pending-admin';
}

export interface TwoSetAccountsResult {
  /** The caller's OWN connections — always present for any signed-in member. */
  personal: Array<ConnectedAccount & { sharedWithOrg?: boolean; sharedTo?: string[] }>;
  /** Org-owned + member-shared connections. Undefined when the caller lacks
   *  integrations.view (orgVisible false). */
  organization?: Array<ConnectedAccount & { via?: 'organization' | 'shared'; sharedBy?: string; sharedByName?: string }>;
  orgVisible: boolean;
  /** C4a: the caller's own pending-admin waits, keyed by toolkit slug. */
  pendingConnections?: Record<string, PendingConnection>;
  /** Own-app PUBLIC client ids (ops-configured; nulls until prest0n's own
   *  OAuth apps exist) — power the one-click Microsoft tenant-approval link
   *  and the exact Google client id in admin instructions. */
  oauthApps?: { microsoftClientId: string | null; googleClientId: string | null };
}

/** Raw two-set listing (no per-slug collapsing — the panel groups itself). */
export async function getAccountsTwoSets(): Promise<TwoSetAccountsResult> {
  const data = await apiRequest<{
    personal?: any[];
    organization?: any[];
    orgVisible?: boolean;
    pendingConnections?: Record<string, PendingConnection>;
    oauthApps?: { microsoftClientId?: string | null; googleClientId?: string | null };
  }>('accounts');
  const norm = (a: any) => ({
    id: a.id,
    status: a.status || 'INITIATED',
    toolkit: {
      slug: a.toolkit?.slug || a.appUniqueId || a.appName || '',
      name: a.toolkit?.name || a.appName || a.appUniqueId || 'Unknown',
    },
    created_at: a.created_at || a.createdAt || '',
    // Signed-in account identity (2026-08-18) — server whitelist-extracts it.
    ...(typeof a.identity === 'string' && a.identity ? { identity: a.identity } : {}),
    sharedWithOrg: !!a.sharedWithOrg,
    // W-R3 (2026-08-25): every workspace this connection is lent to (the
    // sharing member's reverse index) — powers the personal workspace's
    // multi-org share picker.
    ...(Array.isArray(a.sharedTo) && a.sharedTo.length ? { sharedTo: a.sharedTo.filter((x: unknown) => typeof x === 'string') } : {}),
    via: a.via,
    sharedBy: a.sharedBy,
    // Sharer display NAME (never a uid) — server-resolved.
    ...(typeof a.sharedByName === 'string' && a.sharedByName ? { sharedByName: a.sharedByName } : {}),
  });
  return {
    personal: (data.personal || []).map(norm),
    organization: data.organization ? data.organization.map(norm) : undefined,
    orgVisible: !!data.orgVisible,
    ...(data.pendingConnections && typeof data.pendingConnections === 'object'
      ? { pendingConnections: data.pendingConnections }
      : {}),
    ...(data.oauthApps && typeof data.oauthApps === 'object'
      ? {
          oauthApps: {
            microsoftClientId: typeof data.oauthApps.microsoftClientId === 'string' ? data.oauthApps.microsoftClientId : null,
            googleClientId: typeof data.oauthApps.googleClientId === 'string' ? data.oauthApps.googleClientId : null,
          },
        }
      : {}),
  };
}

/** Firm-wide-approval memory: has this firm's admin already approved
 *  prest0n for the toolkit's provider? Reassurance-only — errors read as
 *  "unknown" (null) so callers simply skip the softer copy. */
export async function checkTenantGrant(toolkitSlug: string, address: string): Promise<boolean | null> {
  try {
    const data = await apiRequest<{ granted?: boolean }>('tenant-grant', 'POST', {
      op: 'lookup',
      toolkit: toolkitSlug,
      address,
    });
    return data.granted === true;
  } catch {
    return null;
  }
}

/** C4a: record the caller's "waiting on an admin's approval" entry for a
 *  toolkit (fire-and-forget from timeout paths — a failure only loses the
 *  amber tile, never the flow). */
export async function recordPendingConnection(
  toolkitSlug: string,
  displayName: string,
  intendedAddress: string | null,
): Promise<void> {
  await apiRequest('pending-connection', 'POST', {
    op: 'record',
    toolkit: toolkitSlug,
    displayName,
    ...(intendedAddress ? { intendedAddress } : {}),
  });
}

/** C4a: clear a pending-admin entry (dismiss, or a successful reconnect). */
export async function clearPendingConnection(toolkitSlug: string): Promise<void> {
  await apiRequest('pending-connection', 'POST', { op: 'clear', toolkit: toolkitSlug });
}

/** Share (or unshare) ONE of your own connections with the organization.
 *  Requires the integrations.share permission (403 permission_denied). */
export async function shareConnection(
  connectionId: string,
  share: boolean,
  /** Explicit target org (2026-08-19 eager-org onboarding) — omitted, the
   *  server shares to the caller's scope workspace as before. */
  accountId?: string,
): Promise<{ success: boolean; shared: boolean; app: string }> {
  return apiRequest<{ success: boolean; shared: boolean; app: string }>('share', 'POST', {
    connectionId,
    share,
    ...(accountId ? { accountId } : {}),
  });
}

/** Run the post-connect verification probe for one connection (2026-08-20
 *  connect-journey batch): the server learns WHO actually signed in and
 *  whether that identity has a usable mailbox, persists it, and returns the
 *  verdict. Never throws — verification is an enhancement, so callers get
 *  null on any failure and the connected outcome stands. */
export async function verifyConnection(
  connectionId: string,
  toolkit: string,
  intendedAddress?: string | null,
): Promise<ConnectionVerification | null> {
  try {
    const res = await apiRequest<{ success: boolean; verification: ConnectionVerification }>(
      'verify-connection',
      'POST',
      {
        connectionId,
        toolkit,
        ...(intendedAddress ? { intendedAddress } : {}),
      },
    );
    return res?.verification || null;
  } catch {
    return null;
  }
}

// ============================================================================
// Custom connections (any-system rule, 2026-08-13)
// ============================================================================

/** A system OUTSIDE the catalog, linked via sign-in on Prest0n's VM browser.
 *  Metadata only — credentials never leave the VM's per-user browser profile. */
export interface CustomConnection {
  slug: string;
  name: string;
  url: string | null;
  /** The researched/promoted sign-in destination — the "finish sign-in"
   *  fallback surface reuses it so a re-open never re-resolves cold. */
  signInUrl?: string | null;
  /** 'registered' (never signed in) | 'connected' (sign-in confirmed) |
   *  'signin-lost' (the verifier found the saved browser signed out). Never
   *  fake-connected. */
  status: string;
  connectionKind: 'browser-signin';
  /** Placeholder until the AI-categorization route lands. */
  category: string;
  scope: 'personal' | 'organization';
  access: 'full' | 'read-write' | 'read-only';
  /** Which account was signed in (user-attested at completion). */
  signedInAs?: string | null;
  /** Creator's display name (never a uid) — server-resolved. */
  createdByName?: string | null;
  /** Why a once-connected connection was downgraded: 'signed-out' (the
   *  browser session expired / logged out) | 'profile-missing' (the VM was
   *  rebuilt without the saved profile). Drives the "finish sign-in" copy. */
  signinLostReason?: 'signed-out' | 'profile-missing' | null;
  /**
   * When the saved browser was last actually PROBED (ms epoch), or null when
   * it never has been (2026-09-03).
   *
   * A green tile is a claim, and without this the user cannot tell one
   * verified a minute ago from one nobody has looked at since yesterday —
   * the gap that let the chat say an Oura connection needed re-linking while
   * this page still showed it connected.
   */
  lastVerifiedAtMs?: number | null;
  /**
   * What the last probe actually SAW (2026-09-04): its verdict, the page
   * kind it landed on, why it was held (a live desktop session for the
   * user, a starved workstation, a machine that just came up, a
   * double-check), and how many strikes stand. The tile says why it is or
   * is not sure — "checked 12m ago · sign-in page seen, checking again"
   * instead of a bare timestamp.
   */
  lastProbe?: {
    verdict: string;
    confidence: string;
    kind: string | null;
    held: string | null;
    strikes: number | null;
    atMs: number | null;
  } | null;
}

/** List the workspace's custom (browser-signin) connections. Any member. */
export async function listCustomConnections(): Promise<CustomConnection[]> {
  const data = await apiRequest<{ connections: CustomConnection[] }>('custom-connections');
  return data.connections || [];
}

/** Register a custom connection (requires integrations.manage server-side). */
export async function registerCustomConnection(input: {
  name: string;
  url?: string;
  scope?: 'personal' | 'organization';
  access?: 'full' | 'read-write' | 'read-only';
  signedInAs?: string;
  /** Researched official https sign-in URL — persisted at registration
   *  (2026-08-22) so an abandoned sign-in stays rescuable by the verifier. */
  signInUrl?: string;
  /** W-R3 (2026-08-25): register onto the caller's OWN account-attached
   *  plane ("my connections") instead of the scope workspace. */
  personalOnly?: boolean;
}): Promise<{ success: boolean; slug: string; name: string }> {
  return apiRequest('custom-connections', 'POST', input);
}

/** Remove a custom connection (requires integrations.manage server-side). */
export async function removeCustomConnection(slug: string): Promise<void> {
  await apiRequest<void>(`custom-connections?slug=${encodeURIComponent(slug)}`, 'DELETE');
}

/**
 * Edit ONE custom connection's access, keyed by slug (P0 #6 "access truth",
 * 2026-09-02). The chips used to re-POST the whole registration, and the
 * register path recomputes scope from the body — with `personalOnly` absent
 * that wrote the scope workspace's collection, growing an organization husk
 * beside a personal-only connection. This touches `access` on the doc where
 * it already lives; name, scope and collection are never sent.
 *
 * FEATURE DETECTION (an unknown action falls through to the register path on
 * an older server, which answers 400 'name required' — never a re-register):
 * the caller treats the edit as applied ONLY when the answer carries
 * { ok: true, slug, access }. Any other shape means "not applied".
 */
export async function updateCustomConnectionAccess(
  slug: string,
  access: 'full' | 'read-write' | 'read-only',
): Promise<{ ok?: boolean; slug?: string; access?: string }> {
  return apiRequest('custom-connections', 'POST', { action: 'setAccess', slug, access });
}

/**
 * Record WHICH ACCOUNT a standing browser-lane connection signed into
 * (2026-09-03 owner directive: the identity "needs to be displayed" on both
 * onboarding and the integrations page).
 *
 * The sign-in surface's identity field is optional and usually left empty, so
 * most existing connections carry none — this is how a surface asks for it
 * afterwards, on a connection that already stands. Passing an empty string
 * CLEARS it, for someone who typed the wrong address.
 *
 * Same feature-detection posture as updateCustomConnectionAccess above: an
 * older server has no such branch and falls through to the register path,
 * which refuses a body with no name, so the edit counts as applied ONLY when
 * the answer carries { ok: true }.
 */
export async function updateCustomConnectionIdentity(
  slug: string,
  signedInAs: string,
): Promise<{ ok?: boolean; slug?: string; signedInAs?: string | null }> {
  return apiRequest('custom-connections', 'POST', {
    action: 'setIdentity',
    slug,
    signedInAs: String(signedInAs || '').trim().slice(0, 120),
  });
}

// ── Connect-method research (2026-08-18 connect concierge) ───────────────────

export interface ConnectResearchMethod {
  kind: 'api-key' | 'browser' | 'mcp' | 'desktop-app' | 'mobile' | 'guided';
  title: string;
  pros: string[];
  cons: string[];
  effort: 'minutes' | 'quick' | 'involved';
}

export interface ConnectResearch {
  system: string;
  summary: string;
  recommended: ConnectResearchMethod['kind'];
  methods: ConnectResearchMethod[];
  signInUrl: string;
  apiKeyUrl: string;
  desktopApp: string;
  mcp: string;
  /** Present when the server degraded to the generic browser verdict. */
  degraded?: string;
  /** Research v2 (contract C1, 2026-09-02 — plan P0 #1): what the server's
   *  destination probe saw at signInUrl. Absent on pre-probe rows; the card
   *  labels its primary pill from THIS, never from `recommended`. */
  verifiedKind?: ConnectVerifiedKind;
  /** ISO timestamp of the probe verdict, or null. */
  verifiedAt?: string | null;
  probe?: { kind: 'verdict' | 'transport-error'; at: number; status?: number | null };
  /** Model-facing only — never user copy. */
  probeNote?: string;
  /** '' unless probe-verified as a signup page (§9.41). */
  signupUrl?: string;
  signupVerifiedKind?: string;
}

/** What the destination probe saw at a candidate url (contract C1). */
export type ConnectVerifiedKind = 'login-page' | 'app-host' | 'bot-walled' | 'signup-page' | 'unverified';

/**
 * Quick researched verdict on the BEST way to connect a system with no
 * one-click integration (server-cached; SSOT web-search ladder; failure
 * degrades server-side to an always-valid browser-lane verdict — this call
 * still throws on transport/auth errors, callers own that degrade).
 */
export async function fetchConnectResearch(
  name: string
): Promise<{ research: ConnectResearch; cached: boolean }> {
  return apiRequest('connect-research', 'POST', { name });
}

/**
 * Report a sign-in destination that landed WRONG — off the app, or on a page
 * that is gone (contract C3, 2026-09-02). The body is a hint, never proof:
 * the server applies a strike only when the VM-written journey receipts it
 * reads itself carry that landing, so a report with no matching receipt is
 * logged and answered { applied: false, reason: 'no_receipt' }.
 *
 * NEVER throws and never blocks the sign-in it was observed in: an older
 * server answers 404 'Unknown action', an offline client throws inside — both
 * resolve to null, which callers read as "not applied".
 */
export async function reportDestination(body: {
  app: string;
  url: string;
  landedUrl: string;
  landedKind?: string;
  landedStatus?: number;
  accountId: string;
  journeyId: string;
}): Promise<{ applied: boolean; reason?: string; cleared?: boolean; strikes?: number } | null> {
  try {
    const res = await apiRequest<{ applied?: boolean; reason?: string; cleared?: boolean; strikes?: number }>(
      'report-destination',
      'POST',
      body,
    );
    // Shape check, not just a 2xx: an older server that answered something
    // else must read as "not applied", never as a silent success.
    return res && typeof res.applied === 'boolean'
      ? (res as { applied: boolean; reason?: string; cleared?: boolean; strikes?: number })
      : null;
  } catch {
    return null;
  }
}

/**
 * Initiate OAuth connection to a toolkit
 * Returns a redirect URL for the user to authorize
 */
export async function connectToolkit(
  toolkitSlug: string,
  redirectUrl?: string,
  opts?: {
    /** Two-set model (2026-08-12): 'organization' connects on the WORKSPACE
     *  identity (requires integrations.manage server-side); 'personal' (or
     *  omitted) connects on the caller's own — any member may. Storage
     *  toolkits default to organization server-side for back-compat. */
    target?: 'personal' | 'organization';
    /** Extra setup values some apps need before OAuth (WhatsApp WABA ID,
     *  Zendesk subdomain, …) — collected via getConnectRequiredFields. */
    initFields?: Record<string, string>;
    /** Explicit auto-share target (2026-08-19 eager-org onboarding): the
     *  storage auto-share lands on THIS account instead of the caller's
     *  back-pointer workspace. Permission-checked server-side. */
    shareTo?: string;
  }
): Promise<{ redirectUrl: string; connectedAccountId: string }> {
  return apiRequest('connect', 'POST', {
    toolkit: toolkitSlug,
    redirectUrl: redirectUrl || `${window.location.origin}/integrations/callback`,
    ...(opts?.target ? { target: opts.target } : {}),
    ...(opts?.initFields && Object.keys(opts.initFields).length ? { initFields: opts.initFields } : {}),
    ...(opts?.shareTo ? { shareTo: opts.shareTo } : {}),
  });
}

// ============================================================================
// Connect setup fields (2026-08-14, WhatsApp WABA incident)
// ============================================================================

export interface ConnectInitField {
  name: string;
  displayName: string;
  description: string;
  type: string;
  /** Server-side enrichment (2026-08-17, WhatsApp journey): deep link to the
   *  page where the value lives, its label, and a "don't have one?" branch.
   *  All optional — plain Composio metadata fields carry none of them. */
  helpUrl?: string;
  helpLabel?: string;
  notFoundHint?: string;
}

/** Connect triage (2026-08-17): one selectable situation. showFields: true
 *  pathways proceed to the setup fields; false pathways show guidance for
 *  getting connect-ready instead. */
export interface ConnectPathway {
  id: string;
  label: string;
  guidance: string;
  showFields: boolean;
}

export interface ConnectToolkitHelp {
  prompt: string;
  pathways: ConnectPathway[];
}

export interface ConnectFieldsMeta {
  required: ConnectInitField[];
  optional: ConnectInitField[];
  help?: ConnectToolkitHelp;
}

const _connectFieldsCache = new Map<string, ConnectFieldsMeta>();

/** What a toolkit needs from the user BEFORE its OAuth can start. Empty
 *  required[] = connects with no typed setup. Cached per slug for the
 *  session. `help`, when present, drives the account-type triage chooser. */
/** What the key lane answers. `connected` is the ONLY thing a surface may
 *  render as connected — see functions/src/composio/keyLane.ts. */
export interface KeyConnectResult {
  connected: boolean;
  verdict: 'working' | 'rejected' | 'unverified';
  connectedAccountId: string | null;
  scheme: string;
}

/**
 * Connect an app from a pasted key.
 *
 * Composio answers ACTIVE for a key it has never checked (proven live), so the
 * server mints and then makes a real read-only call before it will say the key
 * works. A `rejected` answer comes back as a 422 with the same body shape, and
 * is a normal outcome here — the user's key was wrong — not a transport error.
 */
export async function connectAppWithKey(
  toolkitSlug: string,
  fields: Record<string, string>,
): Promise<KeyConnectResult> {
  return apiRequest('connect-key', 'POST', {
    toolkit: toolkitSlug.toLowerCase(),
    fields,
  });
}

/**
 * What a toolkit needs from the user before it can be connected.
 *
 * `mode: 'key'` asks the API-KEY lane's question instead of the OAuth one —
 * two different answers for the same slug, which is exactly why the cache is
 * keyed by BOTH. Serving the OAuth answer to the key dialog would render an
 * empty form (the OAuth answer is deliberately empty for these apps) and
 * serving the key answer to the OAuth dialog would ask for a secret the
 * sign-in never uses.
 */
export async function getConnectRequiredFields(
  toolkitSlug: string,
  opts?: { mode?: 'oauth' | 'key' }
): Promise<ConnectFieldsMeta> {
  const slug = toolkitSlug.toLowerCase();
  const mode = opts?.mode === 'key' ? 'key' : 'oauth';
  const cacheKey = `${mode}:${slug}`;
  const cached = _connectFieldsCache.get(cacheKey);
  if (cached) return cached;
  const data = await apiRequest<ConnectFieldsMeta>(
    `connect-fields?toolkit=${encodeURIComponent(slug)}${mode === 'key' ? '&mode=key' : ''}`
  );
  const result: ConnectFieldsMeta = {
    required: data.required || [],
    optional: data.optional || [],
    ...(data.help ? { help: data.help } : {}),
  };
  _connectFieldsCache.set(cacheKey, result);
  return result;
}

/**
 * Disconnect (delete) a connected account
 */
export async function disconnectAccount(connectionId: string): Promise<void> {
  await apiRequest<void>(`accounts/${connectionId}`, 'DELETE');
}

/**
 * Disconnect ALL connections for a toolkit — duplicates included (2026-08-12
 * incident: duplicate Composio connection records made the X button need one
 * click per record; the old per-connection delete removed only the primary).
 * The server deletes every owned connection for the slug in one call.
 */
export async function disconnectToolkit(
  toolkitSlug: string
): Promise<{ success: boolean; deleted: number; attempted: number }> {
  return apiRequest<{ success: boolean; deleted: number; attempted: number }>(
    `toolkits/${encodeURIComponent(toolkitSlug)}`,
    'DELETE'
  );
}

/**
 * Execute a Composio tool action
 */
export async function executeToolAction(
  toolSlug: string,
  input: Record<string, any>
): Promise<any> {
  const data = await apiRequest<{ result: any }>('execute', 'POST', { tool: toolSlug, input });
  return data.result;
}

// ============================================================================
// Storage scope (folder-level access, 2026-08-11)
// ============================================================================

/** Toolkits whose connections carry a folder scope ("What can Preston use?"). */
export const STORAGE_TOOLKIT_SLUGS = new Set<string>([
  'googledrive', 'googledocs', 'googlesheets', 'dropbox', 'box', 'one_drive', 'onedrive',
]);

export function isStorageToolkit(toolkitSlug: string): boolean {
  return STORAGE_TOOLKIT_SLUGS.has(String(toolkitSlug || '').toLowerCase());
}

/** Folder limits are enforceable for the Google Drive corpus ONLY so far —
 *  the server's index/ancestry machinery is Drive-metadata-based and now
 *  refuses folder scopes for other corpora (Round-18). Every surface that
 *  OFFERS folder choosing gates on this, not isStorageToolkit, so Dropbox/
 *  Box/OneDrive users are never sold a limit that can't be enforced. */
export const FOLDER_SCOPE_TOOLKIT_SLUGS = new Set<string>(['googledrive', 'googledocs', 'googlesheets']);
export function supportsFolderScope(toolkitSlug: string): boolean {
  return FOLDER_SCOPE_TOOLKIT_SLUGS.has(String(toolkitSlug || '').toLowerCase());
}

export interface StorageScopeFolder {
  id: string;
  name: string;
  path?: string;
  /** True for a top-level "Shared with me" folder (owned by another account
   *  but shared to the connected one) — set by browse-folders at root. */
  shared?: boolean;
}

export interface StorageScope {
  mode: 'all' | 'folders';
  folders: StorageScopeFolder[];
  /** True when the connection predates scoping (treated as "Everything"). */
  grandfathered?: boolean;
}

/**
 * Read a storage connection's folder scope. Member-visible by design (every
 * member can see the scope chips so denials are never mysterious). Optional
 * connectionId reads one specific connection's scope (multi-account);
 * absent = the primary connection.
 */
export async function getStorageScope(
  appSlug: string,
  connectionId?: string
): Promise<StorageScope> {
  const data = await apiRequest<{ app: string; scope: StorageScope }>(
    `scope?app=${encodeURIComponent(appSlug)}${connectionId ? `&connectionId=${encodeURIComponent(connectionId)}` : ''}`
  );
  return data.scope;
}

/**
 * Set a storage connection's folder scope. Requires integrations.manage
 * (server-enforced); folders are master folders — subfolders and anything
 * added later are included. Optional connectionId targets one specific
 * connection (multi-account); absent = the primary connection.
 */
export async function setStorageScope(
  appSlug: string,
  scope: { mode: 'all' | 'folders'; folders?: StorageScopeFolder[] },
  connectionId?: string
): Promise<StorageScope> {
  const data = await apiRequest<{ app: string; scope: StorageScope }>('scope', 'POST', {
    app: appSlug,
    mode: scope.mode,
    folders: scope.folders || [],
    ...(connectionId ? { connectionId } : {}),
  });
  return data.scope;
}

/**
 * List child folders of a parent for the scope-setup picker. Requires
 * integrations.manage (the picker browses the connected account's whole
 * tree). parentId 'root' lists top-level folders. Optional connectionId
 * browses a specific connection (multi-account); absent = the primary.
 */
export async function browseStorageFolders(
  appSlug: string,
  parentId: string = 'root',
  connectionId?: string
): Promise<StorageScopeFolder[]> {
  const data = await apiRequest<{ folders: StorageScopeFolder[] }>('browse-folders', 'POST', {
    app: appSlug,
    parentId,
    ...(connectionId ? { connectionId } : {}),
  });
  return data.folders || [];
}

// ============================================================================
// Access tier (what Preston may DO in a connection, 2026-08-12)
// ============================================================================

// ── Capability classes (PLAN-connected-apps-governance-20260816 §3) ─────────
// Every tool in every app maps to exactly one of five classes; the three
// tiers survive as PRESETS over them (§3.2), so nothing shipped changes
// meaning. OWNER DECISION: full access ("Everything") stays the default —
// profiles only narrow when explicitly edited.
//
// 2026-08-18 consolidation: this file used to hold a THIRD, CI-ungated copy
// of the capability constants. The single frontend source is now
// roleAppAccessShared.ts — imported/re-exported at the top of this file.
export type AccessTier = SharedAccessTier;
export type AccessProfile = SharedAccessProfile;
export type CapabilityClass = SharedCapabilityClass;

/** THE default for every new connect (founder decision, 2026-08-16 —
 *  reaffirmed 2026-08-18: "Everything" always; profiles only narrow when
 *  explicitly edited). Post-connect copy reads this ONE constant so a future
 *  default change is a one-line swap. */
export const DEFAULT_ACCESS_TIER: AccessTier = 'full';

export interface AccessTierInfo {
  tier: AccessTier;
  /** True when the connection predates tiers (treated as full access). */
  grandfathered?: boolean;
  /** Five-class capability profile (PLAN-connected-apps-governance-20260816
   *  §3). Absent on pre-profile docs — derive with profileFromTier(tier). */
  profile?: AccessProfile;
  /** True when the stored profile matches none of the three presets —
   *  chips render "Custom" and the presets show unselected. */
  custom?: boolean;
}

export const ACCESS_TIER_LABELS: Record<AccessTier, string> = {
  'full': 'Full access',
  'read-write': 'Read + write',
  'read-only': 'Read-only',
};

/**
 * The ONE access-chip label, both axes (owner report 2026-08-20: a Drive
 * scoped to chosen folders still read "Full access" — the label must never
 * claim more than the connection's real reach). The tier says what Preston
 * may DO; the folder scope says what it may SEE. When the scope is narrowed,
 * "certain folders" leads; when the tier is also narrowed, both show.
 * A not-yet-loaded scope falls back to the tier-only label (the chip
 * refines when the scope arrives); a not-yet-loaded access renders '…'.
 */
export function accessChipLabel(
  access: AccessTierInfo | undefined | null,
  scope?: StorageScope | null
): string {
  if (!access) return '…';
  const tierLabel = access.custom ? 'Custom' : (ACCESS_TIER_LABELS[access.tier] || 'Full access');
  if (scope?.mode !== 'folders') return tierLabel;
  const count = scope.folders.length;
  const folderWord = count > 0 ? `Certain folders (${count})` : 'Certain folders';
  if (!access.custom && access.tier === 'full') return folderWord;
  return `${tierLabel} · certain folders`;
}

// Plain-verbs copy (task #55, owner report: "it should be more clear exactly
// what these mean in all circumstances") — every description names what CAN
// happen and what NEVER happens, no jargon.
export const ACCESS_TIER_DESCRIPTIONS: Record<AccessTier, string> = {
  'full': 'Prest0n can do everything this connection allows — read, create, send, and organize. (default)',
  'read-write': 'Prest0n can read anything and add new things — drafts, files, events — but never deletes anything or changes settings.',
  'read-only': 'Prest0n can only look things up. Nothing is ever sent, created, changed, or deleted.',
};

/**
 * Read a connection's access tier. Member-visible by design (same rationale
 * as getStorageScope: every member can see the chip so limits are never
 * mysterious).
 */
export async function getAccessTier(appSlug: string): Promise<AccessTierInfo> {
  const data = await apiRequest<{ app: string; access: AccessTierInfo }>(
    `access?app=${encodeURIComponent(appSlug)}`
  );
  return data.access;
}

/**
 * Set a connection's access tier. Requires integrations.manage
 * (server-enforced; 400 on an invalid tier).
 */
export async function setAccessTier(
  appSlug: string,
  tier: AccessTier
): Promise<AccessTierInfo> {
  const data = await apiRequest<{ app: string; access: AccessTierInfo }>('access', 'POST', {
    app: appSlug,
    tier,
  });
  return data.access;
}

/** Access + the app's per-class tool counts/examples for the fine-tune sheet.
 *  Counts key off the LIVE tool list, never toolkit metadata (§2.3 caution);
 *  null when the backend has no capability map for the app yet. */
export interface AccessDetail {
  access: AccessTierInfo;
  classCounts: Record<CapabilityClass, number> | null;
  classExamples: Record<CapabilityClass, string[]> | null;
}

/**
 * Read a connection's access tier + capability-class breakdown in one call.
 * Same member-visible GET as getAccessTier; a pre-profile backend simply
 * omits classCounts/classExamples and the sheet renders count-less rows.
 */
export async function getAccessDetail(appSlug: string): Promise<AccessDetail> {
  const data = await apiRequest<{
    app: string;
    access: AccessTierInfo;
    classCounts?: Record<CapabilityClass, number> | null;
    classExamples?: Record<CapabilityClass, string[]> | null;
  }>(`access?app=${encodeURIComponent(appSlug)}`);
  return {
    access: data.access,
    classCounts: data.classCounts ?? null,
    classExamples: data.classExamples ?? null,
  };
}

/**
 * Set a connection's five-class profile (fine-tune save). Requires
 * integrations.manage (server-enforced). The server stores the profile plus
 * the matching tier (or custom: true) so tier-only readers keep working.
 */
export async function setAccessProfile(
  appSlug: string,
  profile: AccessProfile
): Promise<AccessTierInfo> {
  const data = await apiRequest<{ app: string; access: AccessTierInfo }>('access', 'POST', {
    app: appSlug,
    profile,
  });
  return data.access;
}

// ============================================================================
// OAuth popup handshake (2026-08-12, T1 live test)
// ============================================================================
// The connect popup (onboarding + in-chat card) is opened with `noopener`,
// so the /integrations/callback page it lands on cannot postMessage back —
// and it used to render the ENTIRE Integrations page inside the popup,
// auto-opening a folder picker there while the opener showed its own scope
// ask (the "asked me twice" report). localStorage is the one channel the
// two windows share: the opener marks the launch, the callback page sees a
// fresh mark, renders a minimal "connected — close this window" landing,
// and leaves every follow-up question to the surface the user started from.

const CONNECT_POPUP_KEY = 'prest0n_connect_popup_v1';
const CONNECT_POPUP_TTL_MS = 15 * 60 * 1000;

/** Called by the OPENER right before window.open on the OAuth URL. The
 *  optional surface lets the callback landing route its can't-close
 *  fallback back to where the user started (2026-08-19, mobile tabs). */
export function markConnectPopupLaunch(toolkitSlug: string, surface?: string): void {
  try {
    window.localStorage.setItem(
      CONNECT_POPUP_KEY,
      JSON.stringify({
        slug: String(toolkitSlug || '').toLowerCase(),
        ts: Date.now(),
        ...(surface ? { surface } : {}),
      }),
    );
  } catch { /* private mode — callback degrades to the full page */ }
}

/** Called by the callback page: fresh mark ⇒ this window is a popup. */
export function readConnectPopupLaunch(): { slug: string; ts: number; surface?: string } | null {
  try {
    const raw = window.localStorage.getItem(CONNECT_POPUP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.slug !== 'string' || typeof parsed?.ts !== 'number') return null;
    if (Date.now() - parsed.ts > CONNECT_POPUP_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearConnectPopupLaunch(): void {
  try {
    window.localStorage.removeItem(CONNECT_POPUP_KEY);
  } catch { /* ignore */ }
}

// ── Add-another-account handshake (task #52) ───────────────────────────────
// The additive connect redirects/pops through the SAME OAuth flow as a first
// connect; the only way to know which connection is NEW afterwards is to
// remember which ids existed before launch and diff the roster on return.

const ADD_ACCOUNT_KEY = 'prest0n_add_account_v1';

/** Called right before launching an ADDITIVE connect for a storage app. */
export function markAddAccountLaunch(toolkitSlug: string, knownConnectionIds: string[]): void {
  try {
    window.localStorage.setItem(
      ADD_ACCOUNT_KEY,
      JSON.stringify({
        slug: String(toolkitSlug || '').toLowerCase(),
        knownIds: knownConnectionIds,
        ts: Date.now(),
      }),
    );
  } catch { /* private mode — the new connection just scopes via its chip */ }
}

export function readAddAccountLaunch(): { slug: string; knownIds: string[]; ts: number } | null {
  try {
    const raw = window.localStorage.getItem(ADD_ACCOUNT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.slug !== 'string' || !Array.isArray(parsed?.knownIds) || typeof parsed?.ts !== 'number') return null;
    if (Date.now() - parsed.ts > CONNECT_POPUP_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearAddAccountLaunch(): void {
  try {
    window.localStorage.removeItem(ADD_ACCOUNT_KEY);
  } catch { /* ignore */ }
}

// ============================================================================
// Toolkit Categories (curated for UI)
// ============================================================================

// ============================================================================
// Law-Firm Curated Integrations
// ============================================================================

/**
 * Workflow-based categories tailored for law firms.
 * Order matters — categories render top-to-bottom in the UI.
 */
export const TOOLKIT_CATEGORIES: Record<string, string[]> = {
  'Communication & Chat': [
    // slack/microsoft_teams LAST (owner directive 2026-08-24): internal-org
    // chat competes with the platform's own group threads — still here, never
    // leading. The curated order IS the likelihood rank (FEATURED_TOOLKITS).
    'gmail', 'outlook', 'zoom', 'whatsapp', 'intercom',
    'slack', 'microsoft_teams',
  ],
  'Calendar & Scheduling': [
    'googlecalendar', 'calendly',
  ],
  'Documents & Storage': [
    'googledrive', 'googledocs', 'googlesheets', 'dropbox',
    'box', 'one_drive', 'notion', 'airtable',
  ],
  // E-signature is a first-class legal/financial workflow (DocuSign holds
  // ~42% US share; it already headlines the Legal industry recommendations
  // but had no curated home). Clio/NetDocuments/iManage/Plaid have NO
  // Composio toolkit (verified 2026-08-20) — do not add them here.
  'Signatures & Agreements': [
    'docusign', 'dropbox_sign', 'pandadoc',
  ],
  'CRM & Client Profiles': [
    'hubspot', 'salesforce', 'zendesk',
  ],
  'Billing & Finance': [
    'stripe', 'quickbooks', 'xero', 'freshbooks',
  ],
  'Tasks & Project Management': [
    'asana', 'monday', 'clickup', 'trello', 'todoist', 'basecamp',
    'jira', 'linear',
  ],
  'Forms & Surveys': [
    'typeform',
  ],
  'Marketing & Collaboration': [
    'mailchimp', 'linkedin', 'youtube',
  ],
  'System Operations': [
    'github', 'gitlab', 'miro', 'figma', 'canva',
  ],
};

/**
 * All curated law-firm apps — union of every category.
 * These show by default on the integrations page (no category filter needed).
 */
export const FEATURED_TOOLKITS: string[] = Array.from(
  new Set(Object.values(TOOLKIT_CATEGORIES).flat())
);

// ── Full-catalog family assignment (2026-08-16 live-test: category chips
// only matched the small curated lists, so every chip view was sub-50 out
// of a 1,209-app catalog). Every toolkit lands in exactly ONE curated
// family: curated placement wins; otherwise the toolkit's own Composio
// category names keyword-map into a family; anything unmatched is the
// 'more apps' tail. First matching rule wins — keep specific before broad.
const FAMILY_KEYWORD_RULES: Array<[RegExp, string]> = [
  [/calendar|schedul/i, 'Calendar & Scheduling'],
  [/form|survey/i, 'Forms & Surveys'],
  // Word-boundary on e-sign so 'design' (d + esign) never matches.
  [/signature|\be-?sign/i, 'Signatures & Agreements'],
  [/chat|messag|communicat|\bemail\b|meeting|video|conferenc|telephon|\bsms\b|voice/i, 'Communication & Chat'],
  [/crm|sales|customer|support|helpdesk|ticket|client/i, 'CRM & Client Profiles'],
  [/bill|financ|account|payment|invoic|payroll|bank|\btax\b|expense/i, 'Billing & Finance'],
  [/document|storage|\bfile\b|note|wiki|knowledge|spreadsheet|sign/i, 'Documents & Storage'],
  [/task|project|productiv|todo|workflow|automation/i, 'Tasks & Project Management'],
  [/market|social|advertis|seo|analytic|collab|design|media/i, 'Marketing & Collaboration'],
  [/dev|code|git|engineer|cloud|infra|monitor|security|data|api|ops/i, 'System Operations'],
];

const CURATED_FAMILY_BY_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(TOOLKIT_CATEGORIES).flatMap(([family, slugs]) =>
    slugs.map((slug) => [slug.toLowerCase(), family]),
  ),
);

/** The uncategorized tail's display family. */
export const MORE_APPS_FAMILY = 'more apps';

/** Resolve the ONE display family a toolkit belongs to. */
export function familyForToolkit(
  tk: Pick<ComposioToolkit, 'slug'> & { meta?: { categories?: Array<{ id: string; name: string }> } },
): string {
  const curated = CURATED_FAMILY_BY_SLUG[tk.slug.toLowerCase()];
  if (curated) return curated;
  const haystack = (tk.meta?.categories || []).map((c) => c?.name || '').join(' ');
  if (haystack) {
    for (const [re, family] of FAMILY_KEYWORD_RULES) {
      if (re.test(haystack)) return family;
    }
  }
  return MORE_APPS_FAMILY;
}

/** Internal-org-communication toolkits (owner directive 2026-08-24): group
 * threads on THIS platform are the org's communication surface, so
 * Slack-class tools are slightly competitive. Connectable, searchable,
 * present in their family — but sorted to the BOTTOM of it and never
 * recommended (the recommendation surfaces filter this same class
 * server-side). Email and consumer messengers are a different thing. */
export const INTERNAL_COMM_TOOLKITS = new Set([
  'slack', 'microsoft_teams', 'discord', 'google_chat', 'googlechat',
  'mattermost', 'rocketchat', 'rocket_chat', 'zulip', 'chanty', 'flock',
  'twist', 'workplace',
]);

/** Most-likely-first ordering: curated featured apps first (in curated
 * order — it IS the rank), then everything else alphabetically; internal
 * comms apps sink to the end of whatever bucket they land in. */
const FEATURED_RANK = new Map(FEATURED_TOOLKITS.map((s, i) => [s.toLowerCase(), i]));
export function sortByLikelihood(a: ComposioToolkit, b: ComposioToolkit): number {
  const ia = INTERNAL_COMM_TOOLKITS.has(a.slug.toLowerCase()) ? 1 : 0;
  const ib = INTERNAL_COMM_TOOLKITS.has(b.slug.toLowerCase()) ? 1 : 0;
  if (ia !== ib) return ia - ib;
  const ra = FEATURED_RANK.get(a.slug.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
  const rb = FEATURED_RANK.get(b.slug.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
  return ra - rb || a.name.localeCompare(b.name);
}
